import "./_internal/CasMultiRowTable.scss";

import { useMemo, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Stack,
  IconButton,
  TableHead,
  StackProps,
  FormHelperText,
  Box,
} from '@mui/material';
import Iconify from 'src/components/iconify';
// hooks
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// utils
import { getEmptyRow } from 'src/compass/shared/util/getEmptyRow';
import { useResponsive } from "src/hooks/use-responsive";
// components
import {
  CasIconButton,
  CasRHFTextField,
  CasRHFAutocomplete,
  CasRHFMultilineTextField,
  CasDisplayField,
} from 'src/compass/shared/components';

export interface IColumns {
  type: 'text' | 'select' | 'component' | 'cells' | 'multilineText' | 'display';
  label: string;
  value?:
    | string
    | number
    | JSX.Element
    | (({ rowNameRef }: { rowNameRef: string }) => string | number | JSX.Element);
  name: string;
  options?: any[];
  inputAdornment?: string;
  inputAdornmentType?: 'endAdornment' | 'startAdornment';
  inputAdornmentPosition?: 'end' | 'start';
  component?: (props: any) => JSX.Element;
  componentProps?: any;
  defaultValue?: any;
  fullWidth?: boolean;
  include?: boolean;
  clearErrorsForOtherField?: string;
}

export interface ICasMultiRowTable {
  readOnly?: boolean;
  title?: string;
  row1Cell1?: any;
  rowCountMode?: 0 | 1 | 2;
  /*
    useFieldArray doesn't respond to form data changes external to its methods.
    replaceData should be used when the table should be updated after an external change.
    NOTES:
      - replaceData uses replace - which causes remount - which loses cursor position
      - The problem isn't useFieldArray. (Try using a TextField instead of CasAutocomplete - and it works)
       It appears to be either 
          (a) Bug in Autocomplete?
          (b) CasAutocomplete implem
  */
  replaceData?: any;
  columns: IColumns[];
  name: string;
  showColumnHeaders?: boolean;
  alignItems?: StackProps['alignItems'];
  onRowDelete?: (index: number) => void;
}

const CasMultiRowTable = ({
  readOnly = false,
  title,
  row1Cell1,
  name,
  rowCountMode = 0,
  columns: parmColumns,
  replaceData,
  showColumnHeaders = true,
  alignItems = 'center',
  onRowDelete,
}: ICasMultiRowTable) => {
  const includedColumns = parmColumns.filter((col) => col.include !== false);

  const [isEmptyRowAdded, setIsEmptyRowAdded] = useState<boolean>(false);
  const { setValue, control, clearErrors } = useFormContext();
  const smDown = useResponsive('down', 'sm');

  const { fields, append, remove, replace } = useFieldArray({ control, name });
  /*
    Note: useFieldArray assigns its own generated unique key
      to each row's "id" property
  */

  // eslint-disable-next-line react-hooks/exhaustive-deps 
  const emptyRow = useMemo(() => getEmptyRow(includedColumns), []);

  useEffect(() => {
    if (replaceData !== undefined) replace(replaceData);
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [replaceData]);

  useEffect(() => {
    if (!isEmptyRowAdded && rowCountMode > 0 && fields.length < rowCountMode) {
      const rowCountToAdd = rowCountMode - fields.length;

      Array.from({ length: rowCountToAdd }).forEach(() => {
        append(emptyRow);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [fields, append]);

  useEffect(() => {
    if (fields.length > 0 && rowCountMode === 2 && row1Cell1)
      setValue(`${name}[0].${includedColumns[0].name}`, row1Cell1);
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [fields, row1Cell1, setValue]);

  const renderColumns = (rowNbr: number) =>
    includedColumns?.map((column: any, colNbr: number) => {
      const standardProps = {
        key: column.name,
        name: `${name}[${rowNbr}].${column.name}`,
        label: smDown ? column.label: "",
        // Add class to reduce padding when field have no label.
        className: smDown ? '' : 'control-no-label',
        disabled: rowNbr === 0 && colNbr === 0 && rowCountMode === 2 ? true : readOnly,
        fullWidth: column.fullWidth || false,
        rowNameRef: `${name}[${rowNbr}]`,
        onChange: column.clearErrorsForOtherField
          ? (_:any, value: any) => {
              if (value) clearErrors(`${name}[${rowNbr}].${column.clearErrorsForOtherField}`);
            }
          : undefined,
      };

      const Component = column?.component;

      const key = `${name}[${rowNbr}].${column.name}`;

      switch (column.type) {
        case 'select':
          return (
            <TableCell key={key}>
              <CasRHFAutocomplete {...standardProps} options={column.options} />
            </TableCell>
          );

        case 'text':
          return (
            <TableCell key={key}>
              <CasRHFTextField {...standardProps} {...(column?.componentProps ?? {})} />
            </TableCell>
          );

        case 'multilineText':
          return (
            <TableCell key={key}>
              <CasRHFMultilineTextField
                {...standardProps}
                minRows={1}
                {...(column?.componentProps ?? {})}
              />
            </TableCell>
          );

        case 'component':
          return (
            <TableCell key={key}>
              <Component {...standardProps} {...(column?.componentProps ?? {})} />
            </TableCell>
          );

        case 'display':
          return (
            <TableCell key={key}>
              <div className='control-no-margin'>
                <CasDisplayField
                  label={standardProps.label}
                  className={standardProps.className}
                  value={
                    <Box textAlign={smDown ? 'inherit' : 'center'}>
                      {typeof column.value === 'function'
                        ? column.value({ rowNameRef: standardProps.rowNameRef })
                        : column.value}
                    </Box>
                  }
                  {...(column?.componentProps ?? {})}
                />
              </div>
            </TableCell>
          );

        case 'cells':
          /* 
            For 'cells', the passed component can return multiple TableCells.
            So the component itself should handle label visibility (e.g., AmountPerTradeOrPercent),
            because CasMultiRowTable doesn't control labels in this case.
          */
          return <Component {...standardProps} />;

        default:
          throw new Error('Unsupported column type');
      }
    });

  const handleRemoveRow = (index: number) => {
    if (onRowDelete) onRowDelete(index);
    setIsEmptyRowAdded(true);
    remove(index);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error } }) => (
        <>
          {error?.message && (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error?.message}
            </FormHelperText>
          )}
          {/* Multi Row table code */}
          <Stack className={`CasMultiRowTable ${smDown && 'multiRowTableCard'}`} alignItems={alignItems}>
            <Table>
              <TableHead>
                {title && (
                  <TableRow className="title">
                    <TableCell colSpan={fields.length}>{title}</TableCell>
                  </TableRow>
                )}
                {!smDown && showColumnHeaders &&  (
                  <TableRow className="table-header-row">
                    {includedColumns.map((column) => (
                      <TableCell key={column.label}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id} sx={{ verticalAlign: 'super' }}>
                    {smDown ? <TableCell>{renderColumns(index)}</TableCell> : renderColumns(index)}
                    <TableCell key={`${name}[${index}].icons`}>
                      {((rowCountMode === 2 && index !== 0 && fields?.length > 2) ||
                        rowCountMode === 0 ||
                        rowCountMode === 1) && (
                        <IconButton
                          disabled={readOnly}
                          className="delete"
                          sx={{ opacity: 0.48, '&:hover': { opacity: 1 } }}
                          onClick={() => handleRemoveRow(index)}
                          color="error"
                        >
                          <Iconify icon="zondicons:minus-outline" width="25" height="25" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CasIconButton
              icon="gridicons:add-outline"
              onClick={() => append(emptyRow)}
              disabled={readOnly}
              color="primary"
            />
          </Stack>
        </>
      )}
    />
  );
};

export default CasMultiRowTable;
